import 'cross-fetch/polyfill';
import { GraphQLClient } from 'graphql-request'

const BASE_URL = process.env.API_BASE_URL
const API_KEY = process.env.API_KEY

const endpoint = BASE_URL + '/graph'

const Client = new GraphQLClient(endpoint, {
  headers: {
    'x-api-key': API_KEY
  },
  mode: 'cors'
})

function sendHTTPRequest(method, url, data = null) {
  url = BASE_URL + url
  let options = {
    method: method,
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY
    }
  }
  if (data) {
    options.body = JSON.stringify(data)
  }

  return fetch(url, options)
}

async function doRequest(query, variables = {}, token = null) {
  try {
    if (token) {
      Client.setHeader('Authorization', 'Bearer ' + token)
    } else {
      Client.setHeader('Authorization', null)
    }
    const response = await Client.request(query, variables)
    if (response.errors) {
      return Promise.reject(response)
    }
    return response
  } catch (error) {
    return Promise.reject(error)
  }
}

async function doAuthorizedRequest(token, query, variables = {}) {
  if (token === undefined || token === null ) {
    return Promise.reject({message: "Invalid token"})
  }
  return await doRequest(query, variables, token)
}

const standardMembershipTypeFields = `
id,
name,
price(inviteCodes: $inviteCodes),
hidePrice,
subscriptionInterval,
validFor,
inviteOnly,
visibility,
type,
requireApproval,
trialDays,
trialUntilDate,
hasTrialPeriod,
trialPaymentSourceRequired,
stopRenewal,
metaValues {
  id,
  key,
  locale,
  value
},
canSwitchTo,
switchTerms(inviteCodes: $inviteCodes) {
  cost,
  validUntil,
  extended
},
requiredFields
optionalFields
`

const membershipTypeUpgradeNeededFields = `
  membershipTypeUpgrades(sort: "order", order: "asc", limit: 0) {
    totalCount
    data {
      id
      newMembershipType {
        id
        name
        price,
        type,
        subscriptionInterval,
        validFor,
        trialDays,
        trialUntilDate,
        hasTrialPeriod,
        trialPaymentSourceRequired,
        stopRenewal,
        metaValues {
          locale
          key
          value
        }
      }
      newCoupon {
        id
        name
        code
        percentOff
        duration
        durationInMonths
        amountOff
        currency
      }
      order
      active

    }
  }
`;

const membershipType = {
  async list (inviteCodes = [], token = null) {
    const query = `
    query MembershipTypes($inviteCodes: [String]){
      membershipTypes(limit: -1, sort: "order", inviteCodes: $inviteCodes) {
        data {
          ${standardMembershipTypeFields}
        }
      }
    }
    `
    return await doRequest(query, {inviteCodes}, token)
  },

  async get (id, inviteCodes= [], token = null) {
    const query = `
    query MembershipType($id: ID!, $inviteCodes: [String]) {
      membershipType(id: $id, inviteCodes: $inviteCodes) {
        ${standardMembershipTypeFields}
        ${membershipTypeUpgradeNeededFields}
      }
    }`

    return doRequest(query, {id, inviteCodes}, token)
  }
}

const membershipCoupon = {
  async check({code, membershipTypeId}) {
    const query = `
    mutation checkCouponCode($code: String!, $membershipTypeId: Int) {
      checkCouponCode(
        code: $code,
        membershipTypeId: $membershipTypeId
      ) {
          id
          code
          percentOff
          amountOff
          duration
          durationInMonths
          currency
      }
    }
    `
    const variables = {
      membershipTypeId: parseInt(membershipTypeId),
      code: code,
    }

    return doRequest(query, variables);
  }
}

const membership = {
  async create(params, token) {
    const query = `
    mutation createMembership($membershipTypeId: Int!, $userId: Int!, $stripeToken: String, $stripeCoupon: String, $storeDiscount: String!, $inviteCodes: [String]) {
      createMembership(
        storeDiscount: $storeDiscount,
        membershipTypeId: $membershipTypeId,
        userId: $userId,
        stripeToken: $stripeToken,
        stripeCoupon: $stripeCoupon,
        inviteCodes: $inviteCodes
      ) {
        id,
        membershipType {
          id,
          name,
          price,
          type,
          subscriptionInterval,
          validFor,
          trialDays,
          trialUntilDate,
          hasTrialPeriod,
          trialPaymentSourceRequired,
          stopRenewal,
          metaValues {
            id,
            key,
            locale,
            value
          }
        },
        validUntil,
        valid,
        awaitingApproval,
        insertedAt
      }
    }
    `
    const variables = {
      membershipTypeId: parseInt(params.membershipTypeId),
      userId: parseInt(params.userId),
      provider: params.provider,
      stripeToken: params.stripeToken,
      stripeCoupon: params.stripeCoupon,
      storeDiscount: params.storeDiscount,
      inviteCodes: params.inviteCodes || [],
    }

    return await doAuthorizedRequest(token, query, variables);
  },

  async complete(params, token) {
    const query = `
    mutation completeMembership($id: Int!) {
      completeMembership(
        id: $id
      ) {
        id,
        membershipType {
          id,
          name,
          price,
          type,
          subscriptionInterval,
          validFor,
          trialDays,
          trialUntilDate,
          hasTrialPeriod,
          trialPaymentSourceRequired,
          stopRenewal,
          metaValues {
            id,
            key,
            locale,
            value
          }
        },
        validUntil,
        valid,
        awaitingApproval,
        insertedAt
      }
    }
    `
    const variables = {
      id: parseInt(params.id),
    }

    return doAuthorizedRequest(token, query, variables)
  },

  async acceptUpgrade(params, token) {
    const query = `
    mutation updateMembership($id: Int!, $stripeToken: String, $membershipTypeUpgradeId: Int!, $inviteCodes: [String]) {
      updateMembership(
        id: $id,
        membership: {
          membershipTypeUpgradeId: $membershipTypeUpgradeId,
          inviteCodes: $inviteCodes,
          stripeToken: $stripeToken
        }
      ) {
        id,
        membershipType {
          id,
          name,
          price,
          type,
          subscriptionInterval,
          validFor,
          trialDays,
          trialUntilDate,
          hasTrialPeriod,
          trialPaymentSourceRequired,
          stopRenewal
        },
        validUntil,
        valid,
        awaitingApproval,
        insertedAt
      }
    }
    `
    let variables = {
      id: parseInt(params.id),
      membershipTypeUpgradeId: parseInt(params.membershipTypeUpgradeId),
      inviteCodes: params.inviteCodes || [],
      stripeToken: params.stripeToken,
    }

    return doAuthorizedRequest(token, query, variables)
  },

  async update(params, token) {
    const query = `
    mutation updateMembership($id: Int!, $stripeToken: String, $storeDiscount: String!, $stripeCoupon: String, $membershipTypeId: Int, $inviteCodes: [String]) {
      updateMembership(
        id: $id,
        membership: {
          storeDiscount: $storeDiscount,
          stripeToken: $stripeToken,
          stripeCoupon: $stripeCoupon,
          membershipTypeId: $membershipTypeId,
          inviteCodes: $inviteCodes
        }
      ) {
        id,
        membershipType {
          id,
          name,
          price,
          type,
          subscriptionInterval,
          validFor,
          trialDays,
          trialUntilDate,
          hasTrialPeriod,
          trialPaymentSourceRequired,
          stopRenewal
        },
        validUntil,
        valid,
        awaitingApproval,
        insertedAt
      }
    }
    `
    const variables = {
      id: parseInt(params.id),
      stripeToken: params.stripeToken,
      storeDiscount: params.storeDiscount,
      stripeCoupon: params.stripeCoupon,
      membershipTypeId: params.membershipTypeId==null ? null : parseInt(params.membershipTypeId),
      inviteCodes: params.inviteCodes || [],
    }

    return doAuthorizedRequest(token, query, variables)
  },

  async cancel(params, token) {
    const query = `
    mutation updateMembership($id: Int!) {
      updateMembership(
        id: $id,
        membership: {
          canceled: true
        }
      ) {
        id,
        membershipType {
          id,
          name,
          price,
          type,
          subscriptionInterval,
          validFor,
          trialDays,
          trialUntilDate,
          hasTrialPeriod,
          trialPaymentSourceRequired,
          stopRenewal
        },
        validUntil,
        insertedAt
      }
    }
    `
    const variables = {
      id: parseInt(params.id),
      // storeDiscount: params.storeDiscount,
    }

    return doAuthorizedRequest(token, query, variables)
  },

  async get(id, token, inviteCodes = []) {
    const query = `
    query getMembership($id: ID!, $inviteCodes: [String]) {
      membership {
        id,
        valid,
        awaitingApproval,
        isSwitchable,
        isExtendable,
        isCancellable,
        isPausable,
        status,
        stripeLastClientSecret,
        membershipType {
          ${standardMembershipTypeFields}
        },
        validUntil,
        currentPeriodStart,
        currentPeriodEnd,
        cancellableAt,
        insertedAt,
        canceled,
        stripePaymentSource {
          brand,
          expMonth,
          expYear,
          last4
        },
        metaValues {
          id,
          key,
          locale,
          value
        },
        payments {
          amount,
          paymentDate,
          membershipType {
            id,
            name,
          },
          prevMembershipType {
            id,
            name,
          },
          receipt {
            number,
            email,
            insertedAt
          }
        }
        ...PlasticCardFragment
      }
    }
    `

    const variables = {
      id: id,
      inviteCodes
    }
    return doAuthorizedRequest(token, query, variables)
  },

  async getPayments(id, token) {
    const query = `
    query getPayments($id: ID!) {
      membership(id: $id) {
        id,
        payments {
          id,
          amount,
          paymentDate,
          membershipType {
            id,
            name,
          },
          prevMembershipType {
            id,
            name,
          },
          receipt {
            id,
            number,
            email
          }
        }
      }
    }
    `
    const variables = {id: id}

    return doAuthorizedRequest(token, query, variables)
  }
}




const standardUserFields = `
  id,
  firstName,
  lastName,
  email,
  address,
  city,
  phone,
  postalCode,
  country,
  state,
  dob,
  industry,
  referredBy,
  referredDetails,
  intro,
  favoriteArtists,
  instagram,
  linkedin,
  website,
  education,
  expectedGraduationDate,
  companyName,
  isTrialAllowed,
  isStripeCustomer,
  ${membershipTypeUpgradeNeededFields}
  upgradeNeeded
  upgradeMethod
  isUpgradeGracePeriod
  membership {
    id,
    valid,
    awaitingApproval,
    status
    stripeLastClientSecret
    canceled,
    isSwitchable,
    isExtendable,
    isCancellable,
    isPausable,
    stripePaymentSource {
      brand,
      expMonth,
      expYear,
      last4
    },
    membershipType {
      id,
      name,
      price,
      type,
      subscriptionInterval,
      validFor,
      trialDays,
      trialUntilDate,
      hasTrialPeriod,
      trialPaymentSourceRequired,
      stopRenewal,
      metaValues {
        locale,
        key,
        value
      },
    },
    validUntil,
    currentPeriodStart,
    currentPeriodEnd,
    cancellableAt,
    insertedAt,
    newMembershipType {
      id,
      name,
      price,
      metaValues {
        locale,
        key,
        value
      }
    },
    upgradeAt
    plasticCard {
      code
      id
    }
  },
  requestedMembership {
    id,
    awaitingApproval,
    status
    membershipType {
      id,
      name,
      price,
      metaValues {
        locale,
        key,
        value
      }
    },
    validUntil,
    insertedAt
  }
`

const account = {
  async login (email, password) {
    const query = `
    mutation login($email: String!, $password: String!) {
      login(email: $email, password: $password) {
        user {
          ${standardUserFields}
        },
        token
      }
    }
    `
    return doRequest(query, {email: email, password: password})
  },

  async resetPassword (email, resetUrl) {
    const query = `
    mutation resetPassword($email: String!, $resetUrl: String!) {
      resetPassword(email: $email, resetUrl: $resetUrl) {
        success,
        errorMessage
      }
    }
    `
    return doRequest(query, {email: email, resetUrl: resetUrl})
  },

  async recoverPassword (password, token) {
    const query = `
    mutation recoverPassword($password: String!, $token: String!) {
      recoverPassword(password: $password, token: $token) {
        success,
        errorMessage
      }
    }
    `
    return doRequest(query, {password: password, token: token})
  },

  async get(id, token) {
    const query = `
    query getUser($id: ID!) {
      user(id: $id) {
        ${standardUserFields}
      }
    }
    `

    const variables = {id: parseInt(id)}
    return doAuthorizedRequest(token, query, variables)
  },

  async create (accountData) {
    const query = `
    mutation register($email: String!, $firstName: String!, $lastName: String!, $password: String!, $country: String, $state: String) {
      register(email: $email, firstName: $firstName, lastName: $lastName, password: $password, country: $country, state: $state) {
        user {
          ${standardUserFields}
        },
        token
      }
    }
    `
    return doRequest(query, accountData)
  },

  async update(id, accountData, token) {
    const query = `
    mutation updateUser($id: Int!, $user: UpdateUserParams!) {
      updateUser(id: $id, user: $user) {
        ${standardUserFields}
      }
    }
    `

    const variables = {
      id: parseInt(id),
      user: accountData
    }

    return doAuthorizedRequest(token, query, variables)
  },

  signUpEmail(data) {
    const creatorId = process.env.EMAIL_CREATOR_ID
    const tags = process.env.EMAIL_TAGS

    data = {
      fields: {
        email: {value: data.email},
        first_name: {value: data.firstName},
        last_name: {value: data.lastName},
        creator_id: {value: creatorId},
        tags: {value: tags}
      }
    }
    return sendHTTPRequest('POST', '/nyc_form_hook', data)
  },

  // NOT USED
  async sendEmailConfirmation(email) {
    const template = process.env.EMAIL_TEMPLATE
    const fromName = process.env.EMAIL_FROM_NAME
    const fromEmail = process.env.EMAIL_FROM_EMAIL
    const subject = process.env.EMAIL_SUBJECT

    const query = `
    mutation sendEmail($template: String!, $subject: String!, $email: String!, $fromEmail: String!, $fromName: String!) {
      sendTransactionalEmail(template: $template, subject: $subject, email: $email, fromEmail: $fromEmail, fromName: $fromName) {
        success,
        errorMessage
      }
    }
    `
    const variables = {
      template: template,
      fromName: fromName,
      fromEmail: fromEmail,
      subject: subject,
      email: email
    }
    return doRequest(query, variables)
  },

  async getPayments(id, token) {
    const query = `
    query getPayments($id: ID!) {
      user(id: $id) {
        id,
        payments(paymentReason: MEMBERSHIP, sort: "inserted_at", order: "desc", limit: -1) {
          totalCount,
          data {
            id,
            amount,
            paymentDate,
            membershipType {
              id,
              name,
            },
            prevMembershipType {
              id,
              name,
            },
            receipt {
              id,
              number,
              email
            }
          }
        }
      }
    }
    `
    const variables = {id: id}

    return doAuthorizedRequest(token, query, variables)
  }
}

const tickets = {
  async checkTicket(code, time) {
    const query = `
    mutation checkTicket($code: String!, $time: String) {
      checkTicket(code: $code, time: $time) {
        code
        id
        numUsages
        qrCode
        status
        ticketType {
          branch {
            key
          }
          id
          name
          timeslotGroupId
        }
        timeslots {
          datetime
          day
          id
          leftUsages
          timeslotDate
          timeslotEnd
          timeslotStart
          valid
        }
        updatedAt
      }
    }
    `
    const variables = {
      code: code,
      time: time,
    }
    return doRequest(query, variables)
  },
}

export {
  membershipType,
  account,
  membership,
  membershipCoupon,
  tickets,
}